import { useCallback, useEffect, useRef, useState } from "react";
import axiosInstant from "../../../api";
import { jiraRoadmapEndpoint } from "../../../api/endpoints";

export interface RoadmapAssignee {
    name: string;
    avatarUrl: string | null;
}

export interface RoadmapSprint {
    id: number;
    name: string;
    state: "closed" | "active" | "future" | "";
    start: string | null;
    end: string | null;
}

export interface RoadmapChild {
    key: string;
    summary: string;
    status: string;
    statusCategory: "new" | "indeterminate" | "done";
    issuetype: string;
    start: string | null;
    due: string | null;
    assignee: RoadmapAssignee | null;
    url: string;
    sprints: RoadmapSprint[];
}

export interface RoadmapEpic {
    key: string;
    summary: string;
    status: string;
    statusCategory: "new" | "indeterminate" | "done";
    start: string | null;
    due: string | null;
    assignee: RoadmapAssignee | null;
    url: string;
    sprints: RoadmapSprint[];
    childStats: { total: number; done: number };
    children: RoadmapChild[];
}

export interface RoadmapResponse {
    configured: boolean;
    projectKey: string;
    fetchedAt?: string;
    sprints: RoadmapSprint[];
    epics: RoadmapEpic[];
}

// Standalone fetch hook (the dashboard's useFetch is period-scoped; the
// roadmap takes no period args). `enabled` gates the initial request so the
// API — and Jira behind it — is only hit once the Roadmap tab is opened.
export function useRoadmap(enabled: boolean) {
    const [data, setData] = useState<RoadmapResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<unknown>(null);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const refetch = useCallback(async (refresh = false) => {
        setLoading(true);
        setError(null);
        try {
            const res = await axiosInstant.get<RoadmapResponse>(jiraRoadmapEndpoint, {
                params: refresh ? { refresh: "1" } : undefined,
            });
            if (mountedRef.current) setData(res.data);
        } catch (err) {
            if (mountedRef.current) setError(err);
        } finally {
            if (mountedRef.current) setLoading(false);
        }
    }, []);

    // Fetch on first activation only; refresh afterwards is manual.
    useEffect(() => {
        if (enabled && !data && !loading && !error) refetch();
    }, [enabled]); // eslint-disable-line

    return { data, loading, error, refetch };
}
